import { Box, Flex, Image, Text, Progress, Button } from "@chakra-ui/react";
import React, { useEffect, useMemo } from "react";
import { ROUTE_NAMES } from "@root/constants";
import { useNavigate } from "react-router-dom";
import { ClockIcon } from "../../assets/icons/clockIcon";
import { ArrowIcon } from "./arrow-icon";
import { PlayIcon } from "./playicon";
import { useTranslation } from "react-i18next";
import { formatDateToISO } from "@root/utils/date-helper";
import defaultThumbnail from "@root/assets/images/Course Thunbnail.png";
import { useCourseManagement } from "@root/hooks/useCourseManagement";
import { CalenderIcon } from "@root/assets/icons";

const DEFAULT_THUMBNAIL = defaultThumbnail;

interface MobileEnrolledCourseProps {
  course: any;
}

const MobileEnrolledCourse: React.FC<MobileEnrolledCourseProps> = ({
  course,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const courseData = course?.course;

  // Fetch chapters and progress for this course to compute video stats
  const {
    courseChaptersData,
    getCourseChapters,
    courseProgressData,
    getCourseProgressById,
  } = useCourseManagement();

  useEffect(() => {
    if (!courseData?.id) return;
    getCourseChapters(Number(courseData.id));
    getCourseProgressById(Number(courseData.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData?.id]);

  // Calculate progress based on individual video progress percentages
  const progressValue = useMemo(() => {
    const chapters = (courseChaptersData as any)?.rows || [];
    const total = chapters.reduce((sum: number, chapter: any) => {
      return sum + (chapter?.courseVideos?.length || 0);
    }, 0);

    if (total === 0) {
      // Fallback to course progress if no chapters loaded yet
      const raw = typeof course?.progress === "number"
        ? course.progress
        : parseFloat(course?.progress || "0");
      return Number.isNaN(raw) ? 0 : Math.round(raw);
    }

    const progressArray = Array.isArray(courseProgressData?.[0])
      ? courseProgressData[0]
      : Array.isArray(courseProgressData)
      ? courseProgressData
      : [];

    // Create a map of video progress by video ID
    const videoProgressMap = new Map<number, number>();
    progressArray.forEach((item: any) => {
      const videoId = item?.video?.id;
      if (videoId !== null && videoId !== undefined) {
        // Treat a completed video as 100%. The in-course player marks completion
        // via the `completed` boolean and can leave the numeric `progress` below
        // 100. Reading only `progress` here made completed videos show 0% on the
        // dashboard while the course view (which reads `completed`) showed 100%.
        // Use `completed` as the source of truth, falling back to numeric progress.
        const rawProgress =
          typeof item?.progress === "number"
            ? item.progress
            : parseFloat(item?.progress || "0");
        const progress = item?.completed === true ? 100 : rawProgress;
        if (!Number.isNaN(progress)) {
          videoProgressMap.set(videoId, Math.max(0, Math.min(100, progress)));
        }
      }
    });

    // Calculate total progress based on individual video progress percentages
    // Each video contributes equally: 100% / total videos
    let totalProgressSum = 0;
    chapters.forEach((chapter: any) => {
      (chapter?.courseVideos || []).forEach((video: any) => {
        if (video?.id) {
          const videoProgress = videoProgressMap.get(video.id) || 0;
          // Each video contributes: video progress / total videos
          totalProgressSum += videoProgress;
        }
      });
    });

    // Calculate average progress: sum of all video progress / total videos
    const calculatedProgress = total > 0 ? totalProgressSum / total : 0;
    return Math.round(calculatedProgress);
  }, [courseChaptersData, courseProgressData, course?.progress]);

  return (
    <Box
      w="100%"
      borderRadius="12px"
      borderWidth="1px"
      borderColor="#CDD6DC"
      bg="white"
      overflow="hidden"
      mb="16px"
    >
      <Flex direction="row" gap="16px" p="16px">
        {/* Image */}
        <Box
          w="120px"
          h="120px"
          flexShrink={0}
          borderRadius="12px"
          overflow="hidden"
        >
          <Image
            src={courseData?.thumbnail || DEFAULT_THUMBNAIL}
            objectFit="contain"
            w="100%"
            h="100%"
          />
        </Box>

        {/* Content */}
        <Flex direction="column" flex="1" gap="8px">
          {/* Title */}
          <Text
            fontSize="16px"
            fontWeight="600"
            color="#2F2F34"
            lineHeight="1.2"
            noOfLines={2}
          >
            {courseData?.title}
          </Text>

          {/* Duration */}
          <Flex alignItems="center" gap="8px">
            <ClockIcon color="#0073E0" boxSize={4} />
            <Text fontSize="14px" color="#5C6670">
              {courseData?.learningDuration || "1 hour 15 minutes"} /{" "}
              {courseData?.learningDuration || "3 hours"}
            </Text>
          </Flex>

          {/* Last Accessed */}
          <Flex alignItems="center" gap="8px">
            <ArrowIcon boxSize={4} />
            <Text fontSize="14px" color="#5C6670">
              {t("certificateSection.lastAccessed")}:{" "}
              {formatDateToISO(courseData?.updatedAt)}
            </Text>
          </Flex>

          {/* Enrollment Period */}
          {courseData?.enrollmentPeriodStart && courseData?.enrollmentPeriodEnd && (
            <Flex alignItems="center" gap="8px" marginTop="6px">
              <CalenderIcon />
              <Text fontSize="14px" fontWeight={400} color="#5C6670">
                {t("courseDetail.enrollmentPeriod")}:{" "}
                <Text as="span" fontWeight={500} fontSize="14px" color="#5C6670">
                  {formatDateToISO(
                    new Date(courseData?.enrollmentPeriodStart),
                  )?.replace(/-/g, ".")}{" "}
                  ~{" "}
                  {formatDateToISO(
                    new Date(courseData?.enrollmentPeriodEnd),
                  )?.replace(/-/g, ".")}
                </Text>
              </Text>
            </Flex>
          )}

          {/* Progress Bar */}
          <Flex alignItems="center" gap="12px">
            <Progress
              value={progressValue}
              height="8px"
              flex="1"
              colorScheme="blue"
              borderRadius="4px"
              bg="#E6E9EF"
            />
            <Text fontSize="14px" color="#2F2F34" fontWeight="500">
              {progressValue}%
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* Continue Learning Button */}
      <Button
        w="100%"
        h="48px"
        bg="#1819B4"
        color="white"
        borderRadius="0"
        fontSize="16px"
        fontWeight="500"
        onClick={() =>
          navigate(`${ROUTE_NAMES.JOURNALIST_COURSE_DETAIL}/${courseData?.id}`)
        }
        leftIcon={<PlayIcon boxSize={4} />}
        _hover={{ bg: "#1415A0" }}
      >
        {t("certificateSection.continueLearning")}
      </Button>
    </Box>
  );
};

export default MobileEnrolledCourse;
